// /tickets/board — kanban columns by workflow state.
// Column counts polled every 30s; realtime subscription on public.tickets
// triggers an immediate column-count refresh on any state/assignee change.

import { rowToTicket, pageBoilerplateFetch } from '../../functions/tickets/ticketUtils.js'
import {
  fetchColumnCounts,
  fetchColumnPage,
  INITIAL_PAGE_SIZE
} from '../../functions/tickets/ticketLoaders.js'
import { subscribeTicketsRealtime } from '../../functions/tickets/subscribeTicketsRealtime.js'

export const home = {
  extends: 'AppPage',
  metadata: { title: 'Board / Tickets / Symbols Workspace' },

  fetch: pageBoilerplateFetch,

  onCreate: async (el, s) => {
    el._presenceUnsub = el.call('useOnlinePresence', { app: 'tickets' })

    const colKeys = (s.root.columns || []).map(c => c.key)
    const totals = await fetchColumnCounts({}, colKeys.length ? colKeys : null)
    s.root.update({ totalByColumn: totals })

    const first = await Promise.all(
      Object.keys(totals).map(async (colKey) => {
        if (!totals[colKey]) return { colKey, rows: [] }
        const rows = await fetchColumnPage(colKey, 0, INITIAL_PAGE_SIZE)
        return { colKey, rows }
      })
    )
    const merged = []
    const loaded = {}
    for (const { colKey, rows } of first) {
      merged.push(...rows.map(rowToTicket))
      loaded[colKey] = rows.length
    }
    s.root.update({ tickets: merged, loadedByColumn: loaded })

    // Realtime: refresh column counts on any ticket state change.
    el._rtUnsub = subscribeTicketsRealtime({
      pageName: 'board',
      tables: ['tickets'],
      onRefresh: async () => {
        const next = await fetchColumnCounts({}, colKeys)
        const cur = s.root.totalByColumn || {}
        const changed = Object.keys(next).some(k => next[k] !== cur[k]) ||
          Object.keys(cur).length !== Object.keys(next).length
        if (changed) s.root.update({ totalByColumn: next })
      }
    })

    el._countTimer = setInterval(async () => {
      // Skip the count refresh entirely when the tab is hidden — there's
      // nothing to draw and we don't want to keep the connection warm.
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
      try {
        const next = await fetchColumnCounts({}, colKeys)
        // Skip the update when nothing actually changed — otherwise
        // every reactive consumer of totalByColumn re-fires on tick.
        const cur = s.root.totalByColumn || {}
        const changed = Object.keys(next).some(k => next[k] !== cur[k]) ||
          Object.keys(cur).length !== Object.keys(next).length
        if (changed) s.root.update({ totalByColumn: next })
      } catch (e) { /* swallow — next tick will retry */ }
    }, 30 * 1000)
  },
  onRemove: (el) => {
    if (el._countTimer) clearInterval(el._countTimer)
    if (el._presenceUnsub) el._presenceUnsub()
    if (el._rtUnsub) { el._rtUnsub(); el._rtUnsub = null }
    const root = el.getRoot()
    if (root && root.selectedTicket) root.update({ selectedTicket: null })
  },

  TicketsSidebar: {},
  TicketsMain: {},
  TicketsNewTicketModal: {},
  TicketsDetailModal: {}
}
